import { useMemo } from 'react';
import {
  Alert,
  Box,
  Grid,
  LinearProgress,
  Paper,
  Stack,
  Typography,
  Chip,
} from '@mui/material';
import {
  PieChart,
  Pie,
  Cell,
  ResponsiveContainer,
  Tooltip as RTooltip,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Legend,
} from 'recharts';
import { useAppSelector } from '../redux/hooks';
import {
  useListTasksQuery,
  useListSprintsQuery,
  useListAssigneesQuery,
} from '../redux/firestoreApi';
import PageHeader from '../components/common/PageHeader';
import { TASK_STATUS_META, TaskStatus } from '../types/models';
import { differenceInCalendarDays, parseISO, isValid } from 'date-fns';

function StatCard({
  title,
  value,
  caption,
  color,
}: {
  title: string;
  value: number | string;
  caption?: string;
  color?: string;
}) {
  return (
    <Paper variant="outlined" sx={{ p: 2.5, height: '100%' }}>
      <Typography variant="caption" color="text.secondary" sx={{ letterSpacing: 0.5 }}>
        {title.toUpperCase()}
      </Typography>
      <Typography variant="h3" sx={{ mt: 1, color: color ?? 'text.primary' }}>
        {value}
      </Typography>
      {caption && (
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
          {caption}
        </Typography>
      )}
    </Paper>
  );
}

export default function DashboardPage() {
  const uid = useAppSelector((s) => s.session.uid)!;
  const projectId = useAppSelector((s) => s.session.activeProjectId);
  const sprintId = useAppSelector((s) => s.session.activeSprintId);

  const sprintsQ = useListSprintsQuery(
    projectId ? { uid, projectId } : (undefined as any),
    { skip: !projectId },
  );
  const tasksQ = useListTasksQuery(
    projectId && sprintId ? { uid, projectId, sprintId } : (undefined as any),
    { skip: !projectId || !sprintId },
  );
  const assigneesQ = useListAssigneesQuery(
    projectId ? { uid, projectId } : (undefined as any),
    { skip: !projectId },
  );

  const sprint = useMemo(
    () => sprintsQ.data?.find((s) => s.id === sprintId) ?? null,
    [sprintsQ.data, sprintId],
  );

  const tasks = tasksQ.data ?? [];

  const counts: Record<TaskStatus, number> = useMemo(() => {
    const c: Record<TaskStatus, number> = {
      PENDING: 0,
      IN_PROGRESS: 0,
      COMPLETED: 0,
      DISCUSSION: 0,
      BLOCKED: 0,
    };
    for (const t of tasks) c[t.status]++;
    return c;
  }, [tasks]);

  const pct = tasks.length === 0 ? 0 : Math.round((counts.COMPLETED / tasks.length) * 100);

  const sprintTiming = useMemo(() => {
    if (!sprint) return null;
    const start = parseISO(sprint.startDate);
    const end = parseISO(sprint.endDate);
    if (!isValid(start) || !isValid(end)) return null;
    const total = Math.max(1, differenceInCalendarDays(end, start));
    const elapsed = Math.min(
      total,
      Math.max(0, differenceInCalendarDays(new Date(), start)),
    );
    return {
      total,
      elapsed,
      remaining: total - elapsed,
      progress: Math.round((elapsed / total) * 100),
    };
  }, [sprint]);

  const pieData = (Object.keys(TASK_STATUS_META) as TaskStatus[]).map((k) => ({
    name: TASK_STATUS_META[k].label,
    value: counts[k],
    color: TASK_STATUS_META[k].color,
  }));

  const byAssignee = useMemo(() => {
    const map = new Map<string, { name: string; total: number; completed: number }>();
    for (const t of tasks) {
      const name = t.assignee?.trim() || 'Unassigned';
      const r = map.get(name) ?? { name, total: 0, completed: 0 };
      r.total++;
      if (t.status === 'COMPLETED') r.completed++;
      map.set(name, r);
    }
    return [...map.values()].sort((a, b) => b.total - a.total);
  }, [tasks]);

  if (!projectId) {
    return (
      <>
        <PageHeader title="Dashboard" />
        <Alert severity="info">Create or select a project to see the dashboard.</Alert>
      </>
    );
  }

  if (!sprintId) {
    return (
      <>
        <PageHeader title="Dashboard" />
        <Alert severity="info">Create or select a sprint to see the dashboard.</Alert>
      </>
    );
  }

  return (
    <>
      <PageHeader
        title={sprint?.sprintName ?? 'Dashboard'}
        subtitle={
          sprint
            ? `${sprint.startDate} → ${sprint.endDate}${
                sprint.sprintGoal ? `  •  ${sprint.sprintGoal}` : ''
              }`
            : undefined
        }
      />

      <Grid container spacing={2} sx={{ mb: 2 }}>
        <Grid item xs={6} md={2.4}>
          <StatCard title="Total tasks" value={tasks.length} />
        </Grid>
        <Grid item xs={6} md={2.4}>
          <StatCard
            title="Completed"
            value={counts.COMPLETED}
            caption={`${pct}% of sprint`}
            color="#2e7d32"
          />
        </Grid>
        <Grid item xs={6} md={2.4}>
          <StatCard
            title="In progress"
            value={counts.IN_PROGRESS}
            color="#f5a623"
          />
        </Grid>
        <Grid item xs={6} md={2.4}>
          <StatCard title="Pending" value={counts.PENDING} color="#757575" />
        </Grid>
        <Grid item xs={6} md={2.4}>
          <StatCard title="Blocked" value={counts.BLOCKED} color="#d32f2f" />
        </Grid>
      </Grid>

      <Grid container spacing={2} sx={{ mb: 2 }}>
        <Grid item xs={12} md={6}>
          <Paper variant="outlined" sx={{ p: 3, height: '100%' }}>
            <Stack
              direction="row"
              justifyContent="space-between"
              alignItems="baseline"
              sx={{ mb: 2 }}
            >
              <Typography variant="h6">Sprint progress</Typography>
              <Chip
                label={`${pct}% complete`}
                color={pct >= 70 ? 'success' : pct >= 30 ? 'warning' : 'error'}
              />
            </Stack>
            <LinearProgress
              variant="determinate"
              value={pct}
              sx={{ height: 12, borderRadius: 6, mb: 3 }}
            />
            {sprintTiming && (
              <Stack spacing={1}>
                <Stack direction="row" justifyContent="space-between">
                  <Typography color="text.secondary">Timeline</Typography>
                  <Typography>
                    Day {sprintTiming.elapsed} of {sprintTiming.total} —{' '}
                    {sprintTiming.remaining} left
                  </Typography>
                </Stack>
                <LinearProgress
                  variant="determinate"
                  value={sprintTiming.progress}
                  color="info"
                  sx={{ height: 8, borderRadius: 4 }}
                />
              </Stack>
            )}
          </Paper>
        </Grid>

        <Grid item xs={12} md={6}>
          <Paper variant="outlined" sx={{ p: 3, height: '100%' }}>
            <Typography variant="h6" sx={{ mb: 2 }}>
              Status distribution
            </Typography>
            <Box sx={{ height: 240 }}>
              <ResponsiveContainer>
                <PieChart>
                  <Pie
                    data={pieData}
                    innerRadius={55}
                    outerRadius={85}
                    paddingAngle={2}
                    dataKey="value"
                  >
                    {pieData.map((d, i) => (
                      <Cell key={i} fill={d.color} />
                    ))}
                  </Pie>
                  <RTooltip />
                  <Legend />
                </PieChart>
              </ResponsiveContainer>
            </Box>
          </Paper>
        </Grid>
      </Grid>

      <Grid container spacing={2}>
        <Grid item xs={12}>
          <Paper variant="outlined" sx={{ p: 3 }}>
            <Typography variant="h6" sx={{ mb: 2 }}>
              Team distribution
            </Typography>
            <Box sx={{ height: 320 }}>
              <ResponsiveContainer>
                <BarChart data={byAssignee}>
                  <CartesianGrid strokeDasharray="3 3" opacity={0.3} />
                  <XAxis dataKey="name" />
                  <YAxis allowDecimals={false} />
                  <RTooltip />
                  <Legend />
                  <Bar dataKey="total" name="Total" fill="#5b6cff" />
                  <Bar dataKey="completed" name="Completed" fill="#2e7d32" />
                </BarChart>
              </ResponsiveContainer>
            </Box>
            <Stack direction="row" spacing={1} flexWrap="wrap" sx={{ mt: 2 }}>
              {assigneesQ.data?.map((a) => (
                <Chip
                  key={a.id}
                  size="small"
                  label={a.name}
                  sx={{ bgcolor: `${a.color}22`, color: a.color }}
                />
              ))}
            </Stack>
          </Paper>
        </Grid>
      </Grid>
    </>
  );
}
